import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Printer, ClipboardList, Warehouse, FileDown, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { downloadFile, formatApiError } from "@/lib/api";

export const WarehousePrintGrid = ({ warehouses, onPrint, onPrintUnmatched, busyId }) => {
  const list = warehouses.filter((w) => w.warehouse_id !== "UNMATCHED");
  const un = warehouses.find((w) => w.warehouse_id === "UNMATCHED");
  if (!list.length && !un) return null;
  return (
    <div className="space-y-3" data-testid="warehouse-print-section">
      <h2 className="font-heading text-lg font-bold uppercase tracking-tight sm:text-xl">Print by warehouse</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {list.map((w) => (
          <div key={w.warehouse_id} data-testid={`warehouse-card-${w.warehouse_id}`} className="stat-card flex h-full flex-col justify-between rounded-md border border-slate-800 bg-[#111827] p-4 hover:border-blue-500/50">
            <div>
              <div className="flex items-center gap-2 font-mono text-sm font-semibold text-blue-300"><Warehouse className="h-4 w-4" />{w.warehouse_id}</div>
              <div className="mt-2 flex gap-3 font-mono text-xs text-slate-400">
                <span className="text-amber-300">{w.pending} pending</span>
                <span className="text-emerald-400">{w.printed} printed</span>
                {w.duplicates > 0 && <span className="text-red-400">{w.duplicates} dup</span>}
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <Button data-testid={`warehouse-print-${w.warehouse_id}`} size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={!w.pending || busyId === w.warehouse_id}
                onClick={() => onPrint(w.warehouse_id)}>
                <Printer className="mr-1 h-3.5 w-3.5" /> Print {w.pending}
              </Button>
              <Button asChild size="sm" variant="outline" className="border-slate-700 bg-transparent hover:bg-slate-800">
                <Link to={`/picklist?warehouse=${encodeURIComponent(w.warehouse_id)}&date=all`} data-testid={`warehouse-picklist-${w.warehouse_id}`} title="Picklist"><ClipboardList className="h-3.5 w-3.5" /></Link>
              </Button>
              <Button size="sm" variant="outline" className="border-slate-700 bg-transparent hover:bg-slate-800" title="Download picklist PDF" data-testid={`warehouse-picklist-pdf-${w.warehouse_id}`}
                onClick={() => downloadFile(`/picklist/pdf?status=pending&warehouse_id=${encodeURIComponent(w.warehouse_id)}`, `picklist-${w.warehouse_id}.pdf`).catch((e) => toast.error(formatApiError(e)))}>
                <FileDown className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {un && (
          <div data-testid="warehouse-card-UNMATCHED" className="stat-card flex h-full flex-col justify-between rounded-md border border-red-800/60 bg-[#1a1114] p-4 hover:border-red-500/50">
            <div>
              <div className="flex items-center gap-2 font-mono text-sm font-semibold text-red-300"><AlertTriangle className="h-4 w-4" />UNMATCHED</div>
              <div className="mt-1 text-[11px] text-slate-400">Unassigned or unreadable SKU</div>
              <div className="mt-2 flex gap-3 font-mono text-xs text-slate-400">
                <span className="text-amber-300">{un.pending} pending</span>
                <span className="text-emerald-400">{un.printed} printed</span>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <Button data-testid="warehouse-print-UNMATCHED" size="sm" className="flex-1 bg-red-700 hover:bg-red-600" disabled={!un.pending || busyId === "UNMATCHED"} onClick={onPrintUnmatched}>
                <Printer className="mr-1 h-3.5 w-3.5" /> Print {un.pending}
              </Button>
              <Button asChild size="sm" variant="outline" className="border-slate-700 bg-transparent hover:bg-slate-800">
                <Link to="/labels?warehouse=unmatched&status=pending&date=all" data-testid="warehouse-fix-UNMATCHED" title="Assign manually">Fix</Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
